import { bmrCalculatorKeys as k } from "@/i18n/bmr-calculator-keys"

/**
 * Basal metabolic rate: the energy the body uses at complete rest, and the
 * daily total once activity is added on top.
 *
 * Pure data + arithmetic, and in `lib/` rather than beside the route so the
 * dependency points the way the rest of the tree does (app -> lib, never
 * back): the widget, the on-page reference table AND the markdown mirror in
 * `lib/native-markdown/bmr-calculator.ts` all read this one source, so a
 * changed coefficient cannot drift between them. It is also unit-testable
 * without rendering React.
 *
 * Two equations ship. Mifflin-St Jeor (1990) is the primary one: it is what
 * current dietetic practice uses, having been shown more accurate than
 * Harris-Benedict in validation studies. Revised Harris-Benedict
 * (Roza & Shizgal, 1984) is shown alongside because it is the number most
 * other calculators print, and a reader comparing tools should see why two
 * sites disagree rather than assume one is broken.
 *
 * Both were derived on adults and on binary sex categories — the page says so
 * in `bmr_limit4Body` rather than leaving it implied.
 */

/**
 * Plausibility bounds. Outside these the input is almost certainly a typo, and
 * the equations are not validated there anyway.
 */
export const MIN_AGE = 15
export const MAX_AGE = 100
export const MIN_HEIGHT_CM = 90
export const MAX_HEIGHT_CM = 250
export const MIN_WEIGHT_KG = 20
export const MAX_WEIGHT_KG = 400

/**
 * The sex term in both equations. Named for what it is — an input the
 * published formulas take, derived from population averages in body
 * composition — not a claim about the reader.
 */
export type BmrSex = "female" | "male"

export interface BmrInput {
  sex: BmrSex
  /** Years. */
  age: number
  heightCm: number
  weightKg: number
}

/**
 * Mifflin-St Jeor (1990), in kcal/day.
 *
 *   male:   10·kg + 6.25·cm − 5·age + 5
 *   female: 10·kg + 6.25·cm − 5·age − 161
 */
export function mifflinStJeor({
  sex,
  age,
  heightCm,
  weightKg,
}: BmrInput): number {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age
  return sex === "male" ? base + 5 : base - 161
}

/**
 * Revised Harris-Benedict (Roza & Shizgal, 1984), in kcal/day.
 *
 *   male:   88.362 + 13.397·kg + 4.799·cm − 5.677·age
 *   female: 447.593 +  9.247·kg + 3.098·cm − 4.330·age
 */
export function harrisBenedict({
  sex,
  age,
  heightCm,
  weightKg,
}: BmrInput): number {
  return sex === "male"
    ? 88.362 + 13.397 * weightKg + 4.799 * heightCm - 5.677 * age
    : 447.593 + 9.247 * weightKg + 3.098 * heightCm - 4.33 * age
}

/**
 * Calories as the reader sees them: whole kcal.
 *
 * Callers must derive the daily total from THIS value, not from the raw one —
 * see `dailyCalories`.
 */
export function roundCalories(kcal: number): number {
  return Math.round(kcal)
}

/**
 * Activity multipliers, the standard Harris-Benedict set.
 *
 * `as const` keeps each `*Key` at its literal type. Widening them to the
 * namespace's whole key union pulls in messages that take ICU placeholders,
 * and next-intl then demands a `values` argument at every `t(level.labelKey)`
 * call site.
 */
const ACTIVITY_DEFS = [
  {
    id: "sedentary",
    factor: 1.2,
    labelKey: k.activitySedentary,
    descKey: k.activitySedentaryDesc,
  },
  {
    id: "light",
    factor: 1.375,
    labelKey: k.activityLight,
    descKey: k.activityLightDesc,
  },
  {
    id: "moderate",
    factor: 1.55,
    labelKey: k.activityModerate,
    descKey: k.activityModerateDesc,
  },
  {
    id: "very",
    factor: 1.725,
    labelKey: k.activityVery,
    descKey: k.activityVeryDesc,
  },
  {
    id: "extra",
    factor: 1.9,
    labelKey: k.activityExtra,
    descKey: k.activityExtraDesc,
  },
] as const

export type ActivityDef = (typeof ACTIVITY_DEFS)[number]
export type ActivityLevel = ActivityDef["id"]

export const ACTIVITY_LEVELS: readonly ActivityDef[] = ACTIVITY_DEFS

export const ACTIVITY_BY_ID: Record<ActivityLevel, ActivityDef> =
  Object.fromEntries(ACTIVITY_DEFS.map((a) => [a.id, a])) as Record<
    ActivityLevel,
    ActivityDef
  >

/** Sensible default: most people asking are not training six days a week. */
export const DEFAULT_ACTIVITY: ActivityLevel = "light"

/**
 * Daily calorie need (TDEE) = BMR × activity factor.
 *
 * Takes the ROUNDED BMR on purpose, so the arithmetic on the page is
 * reproducible by hand: a reader who sees "1 650 kcal" and a ×1.55 multiplier
 * gets the number the page prints. Deriving it from the unrounded BMR instead
 * would leave the two off by a calorie or two with nothing on screen to
 * explain the gap — the same display-versus-computation trap that put a
 * "25.0" under a "healthy weight" label on the BMI page.
 */
export function dailyCalories(
  roundedBmr: number,
  level: ActivityLevel
): number {
  return roundCalories(roundedBmr * ACTIVITY_BY_ID[level].factor)
}

/** Whether an input set is inside the range the equations are meant for. */
export function isPlausible({ age, heightCm, weightKg }: BmrInput): boolean {
  return (
    age >= MIN_AGE &&
    age <= MAX_AGE &&
    heightCm >= MIN_HEIGHT_CM &&
    heightCm <= MAX_HEIGHT_CM &&
    weightKg >= MIN_WEIGHT_KG &&
    weightKg <= MAX_WEIGHT_KG
  )
}
